import {
  fetchUserEducations,
  getLevelsForAppID,
  getQuizQuestionsFor,
  userDidCompleteLevel,
} from "../../Models/firebaseHelper";
import i18next from "i18next";
import { trackEvent } from "../../util/mixpanel";
import { retryAsync } from "../../util/retryAsync";
import type {
  Course,
  CourseData,
  Level,
  GenericLevelId,
  QuestionAnswerStates,
} from "./Education.types";

async function getSortedLevels(
  course: Course,
  courseData: CourseData
): Promise<{
  sortedLevels: Level[];
  totalLevels: number;
}> {
  const courseLevels = await getLevelsForAppID(course.ID);

  if (courseData.span === "annualUpdate") {
    const annualUpdateLevels = (() => {
      let shouldExclude = false;
      return courseLevels
        .filter((level) => {
          // If we encounter a theory level
          if (level.LevelType === "theory") {
            // Check if this theory should be excluded
            shouldExclude =
              courseData.excludedTheoryIds?.includes(level.TheoryID) ?? false;
            // Include theory if it's mandatory or not excluded

            return level.IsMandatory || !shouldExclude;
          }
          // If we encounter a diagnostic test, reset the exclusion flag
          if (level.LevelType === "diagnosticTest") {
            shouldExclude = false;
            return true;
          }
          // For support levels, include only if we're not in exclusion mode
          return !shouldExclude;
        })
        .filter((level) => level.LevelType !== "quizOnWrongAnswers")
        .map((level, index) => ({
          ...level,
          TotalLevelIndex: index,
        }));
    })();

    return {
      sortedLevels: annualUpdateLevels,
      totalLevels: annualUpdateLevels.length,
    };
  } else {
    var sortedLevels = courseLevels.filter(
      (level) => level.LevelType !== "diagnosticTest"
    );

    // Recalculate indexes after filtering
    let currentChapterIndex = -1;
    let levelIndexInChapter = 0;

    sortedLevels = sortedLevels.map((level, totalIndex) => {
      // Reset LevelIndex when we encounter a new chapter
      if (level.ChapterIndex !== currentChapterIndex) {
        currentChapterIndex = level.ChapterIndex;
        levelIndexInChapter = 0;
      } else {
        levelIndexInChapter++;
      }

      return {
        ...level,
        TotalLevelIndex: totalIndex,
        LevelIndex: levelIndexInChapter,
      };
    });

    return {
      sortedLevels: sortedLevels,
      totalLevels: sortedLevels.length,
    };
  }
}

function checkIfLevelIsLocked(
  level: Level,
  highestLevelCompleted: number
): string | null {
  const t = i18next.t.bind(i18next);

  if (level.LevelType === "diagnosticTest") {
    // These tests are only available after to do once.
    return highestLevelCompleted !== level.TotalLevelIndex - 1
      ? t("educationContent.confirationPopup.messageDiagnostic")
      : null;
  }

  return highestLevelCompleted + 1 < level.TotalLevelIndex
    ? t("educationContent.confirationPopup.message")
    : null;
}

async function handleLevelClick(
  course: Course,
  level: Level,
  highestLevelCompleted: number
): Promise<GenericLevelId | null> {
  const isLevelLocked = checkIfLevelIsLocked(level, highestLevelCompleted);
  const analyticsParams = {
    courseTitle: course.Title,
    courseId: course.ID,
    levelId: level.ID,
    educationId: level.AppID,
    chapterIndex: level.ChapterIndex,
    levelIndex: level.LevelIndex,
    levelType: level.LevelType,
    totalLevelIndex: level.TotalLevelIndex,
  };

  if (isLevelLocked) {
    trackEvent("WebApp: Level locked clicked", analyticsParams);
    return null;
  }

  trackEvent("WebApp: Level started", analyticsParams);

  return await handleLevelType(level);
}

async function handleLevelType(level: Level): Promise<GenericLevelId> {
  switch (level.LevelType) {
    case "AIChat":
      return level.TheoryID;
    case "AIRolePlay":
      // Return tuple [TheoryID, RolePlayID] when available so frontend can fetch the exact roleplay
      return level.RolePlayID
        ? [level.TheoryID, level.RolePlayID]
        : level.TheoryID;
    case "branchingStory":
      // Return tuple [TheoryID, BranchingStoryID] when available
      return level.BranchingStoryID
        ? [level.TheoryID, level.BranchingStoryID]
        : level.TheoryID;
    case "theory":
      return level.TheoryID;
    case "video":
      return level.VideoURL || level.VideoID || null;
    case "questionsLoop":
      return level.QuestionLoopIds;
    case "wordlist":
      return level.WordListIDs;
    case "questionsCaseStudy":
    case "questionsLinearAnswerMode":
      return level.QuestionIDs;
    case "diagnosticTest":
      return Object.values(level.DiagnosticQuestions || {}).flat() as string[];
    // return level.DiagnosticQuestions || {};
    case "quizOnWrongAnswers":
      return await getQuizQuestionsFor(level.ChapterIndex, level.AppID);
    default:
      console.warn("Unknown level type:", level.LevelType);
      return null;
  }
}

async function handleDidCompleteLevel(
  level: Level,
  course: Course,
  courseData: CourseData,
  totalNumberOfLevels: number,
  questionAnswerStates: QuestionAnswerStates,
  startTime?: number
): Promise<boolean> {
  // Validate TotalLevelIndex to prevent null values being sent to backend
  if (
    typeof level.TotalLevelIndex !== "number" ||
    !Number.isFinite(level.TotalLevelIndex) ||
    level.TotalLevelIndex < 0
  ) {
    console.error("Invalid level: TotalLevelIndex is missing or invalid", {
      levelId: level.ID,
      levelType: level.LevelType,
      totalLevelIndex: level.TotalLevelIndex,
      courseId: courseData.ID,
      level: level,
    });
    alert(
      "Ett tekniskt fel uppstod. Vänligen ladda om sidan och försök igen. Om problemet kvarstår, kontakta support."
    );
    return false;
  }

  const highestLevelCompleted = level.TotalLevelIndex;
  const courseId = courseData.ID;
  const isAlreadyCompleted =
    highestLevelCompleted <= courseData.highestLevelCompleted;
  const allCompleted = highestLevelCompleted + 1 >= totalNumberOfLevels;

  const analyticsParams = {
    courseTitle: course.Title,
    courseId: course.ID,
    levelId: level.ID,
    educationId: courseData.ID,
    chapterIndex: level.ChapterIndex,
    levelIndex: level.LevelIndex,
    levelType: level.LevelType,
    totalLevelIndex: level.TotalLevelIndex,
    isAlreadyCompleted: isAlreadyCompleted,
    highestLevelCompleted: highestLevelCompleted,
    allCompleted: allCompleted,
  };

  trackEvent("WebApp: Level completed", analyticsParams);

  // WARNING: HARD
  if (isAlreadyCompleted) {
    return false;
  } else if (allCompleted) {
    trackEvent("WebApp: Level all completed", analyticsParams);
  }

  try {
    await userDidCompleteLevel(
      highestLevelCompleted,
      courseId,
      level.TotalLevelIndex,
      questionAnswerStates,
      level.DiagnosticQuestions || {},
      startTime,
      level.LevelType,
      level.ID
    );
    return true;
  } catch (error) {
    console.error("Error completing level:", error);
    alert(error);
    return false;
  }
}

async function reloadCurrentCourse(courseId: string): Promise<any | undefined> {
  const userCourses = await retryAsync(() => fetchUserEducations());
  return userCourses?.find((userCourse) => userCourse.ID === courseId);
}

export {
  checkIfLevelIsLocked,
  getSortedLevels,
  handleDidCompleteLevel,
  handleLevelClick,
  handleLevelType,
  reloadCurrentCourse,
};
